import { EVerticalNames } from '@constants';
import { IUserInfo, IUserSubject, IUser } from '@sdk/contracts';
import { Form, FormInstance, Select, Button, Space, InputNumber } from 'antd';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { RegionSpecificFields } from './components/RegionSpecificFields';
import {
  Wrapper,
  SubjectSelectWrapper,
  NoSpinnerInputWrapper,
  SubjectsBlock,
  TitleWrapper
} from './UniFormContent.styles';

import {
  UserInfoBranchSelector,
  UserInfoDegreeSelector,
  UserInfoUniversitySelector
} from '@/components/Form/UserInfo';
import { useRegionalization } from '@/hooks';
import { useAllCoursesForStudent } from '@/store';
import { useUserInfoByUser } from '@/store/users/user-info/useUserInfo';

type UniFormContentProps = {
  form: FormInstance<IUserInfo>;
  profile?: IUser;
  isManager: boolean;
};

export const UniFormContent: React.FC<UniFormContentProps> = ({ form, profile, isManager }) => {
  const { t } = useTranslation(['DefaultTranslations', 'StudentProfileConfigurationTranslations']);
  const { uni: uniRegionConfig } = useRegionalization();
  const { courses } = useAllCoursesForStudent(profile!);
  const { userInfo } = useUserInfoByUser(profile?._id);
  const [subjects, setSubjects] = useState<IUserSubject[]>(
    userInfo?.subjects?.length
      ? userInfo.subjects.map(s => ({ course: s.course?._id ?? '', note: s.note ?? 0 }))
      : [{ course: '', note: 0 }]
  );

  useEffect(() => {
    if (userInfo?.subjects?.length) {
      setSubjects(userInfo.subjects.map(s => ({ course: s.course?._id ?? '', note: s.note ?? 0 })));
    }
  }, [userInfo]);

  useEffect(() => {
    form.setFieldValue(['subjects'], subjects);
  }, [form, subjects]);

  const handleAddSubject = () => {
    setSubjects(prev => [...prev, { course: '', note: 0 }]);
  };

  const handleRemoveSubject = (idx: number) => {
    const newSubjects = subjects.filter((_, i) => i !== idx);
    setSubjects(newSubjects);
    form.setFieldValue(['subjects'], newSubjects);
  };

  const handleSubjectChange = (idx: number, field: keyof IUserSubject, value: string | number) => {
    const newSubjects = [...subjects];
    newSubjects[idx][field] = value as never;
    setSubjects(newSubjects);
    form.setFieldValue(['subjects'], newSubjects);
  };

  const handleBranchChange = () => {
    form.setFieldValue(['degree'], undefined);
    form.setFieldValue(['university'], undefined);
  };

  return (
    <Wrapper>
      {isManager && <TitleWrapper>{t(EVerticalNames.UNI)}</TitleWrapper>}
      <Form.Item
        name={['studyBranch']}
        label={t('StudentProfileConfigurationTranslations:STUDY_BRANCH')}
        rules={[
          { required: true, message: t('StudentProfileConfigurationTranslations:STUDY_BRANCH') }
        ]}>
        <UserInfoBranchSelector onChange={handleBranchChange} />
      </Form.Item>

      <Form.Item
        name={['degree']}
        label={t('StudentProfileConfigurationTranslations:DEGREE')}
        rules={[{ required: true, message: t('StudentProfileConfigurationTranslations:DEGREE') }]}>
        <UserInfoDegreeSelector />
      </Form.Item>

      <Form.Item
        name={['university']}
        label={t('StudentProfileConfigurationTranslations:UNIVERSITY')}
        rules={[
          { required: true, message: t('StudentProfileConfigurationTranslations:UNIVERSITY') }
        ]}>
        <UserInfoUniversitySelector />
      </Form.Item>
      <RegionSpecificFields form={form} userInfo={userInfo} />
      <SubjectsBlock>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {t('StudentProfileConfigurationTranslations:SUBJECTS_TITLE')}
        </div>
        {subjects.map((subject, idx) => (
          <Space key={idx} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
            <Form.Item
              name={['subjects', idx, 'course']}
              rules={[
                { required: true, message: t('StudentProfileConfigurationTranslations:SUBJECT') }
              ]}
              style={{ minWidth: 200 }}>
              <SubjectSelectWrapper>
                <Select
                  options={courses?.map(course => ({
                    label: course.name,
                    value: course._id
                  }))}
                  placeholder={t('StudentProfileConfigurationTranslations:SUBJECT')}
                  value={subject.course}
                  onChange={value => handleSubjectChange(idx, 'course', value)}
                />
              </SubjectSelectWrapper>
            </Form.Item>
            <Form.Item
              name={['subjects', idx, 'note']}
              rules={[
                { required: true, message: t('StudentProfileConfigurationTranslations:GRADE') }
              ]}
              style={{ width: 55 }}>
              <NoSpinnerInputWrapper>
                <InputNumber
                  min={uniRegionConfig.grade.min}
                  max={uniRegionConfig.grade.max}
                  placeholder={t('StudentProfileConfigurationTranslations:GRADE')}
                  value={subject.note}
                  onChange={value => handleSubjectChange(idx, 'note', value || 0)}
                />
              </NoSpinnerInputWrapper>
            </Form.Item>
            {subject.course && (
              <Button type="default" onClick={() => handleRemoveSubject(idx)}>
                {t('StudentProfileConfigurationTranslations:REMOVE_SUBJECT')}
              </Button>
            )}
          </Space>
        ))}
        <Button type="dashed" onClick={handleAddSubject} style={{ width: '100%' }}>
          {t('StudentProfileConfigurationTranslations:ADD_SUBJECT')}
        </Button>
      </SubjectsBlock>
    </Wrapper>
  );
};
